/**
 * Why a key stopped working, for a key that has a `revokedAt`.
 *
 * The distinction exists for the reader, not the authenticator: both values fail a
 * request identically, and `_auth` never branches on this. It is what lets the
 * management surface say "you revoked this" rather than reporting an idle key the
 * system took out of service as something the owner did.
 *
 * Recorded on the row rather than derived from audit history, so a status can be
 * shown without replaying events.
 */
export enum RevokedReason {
	/** The owner revoked it, typically the second half of a rotation. */
	USER_REVOKED = 'USER_REVOKED',
	/** The sweep took it out of service for going unused past the idle threshold. */
	IDLE_BLOCKED = 'IDLE_BLOCKED',
	/**
	 * The sweep recorded that the key's `expires_at` had passed.
	 *
	 * Bookkeeping, not a decision: expiry already rejected the key from the moment
	 * the timestamp passed, and this only writes that down so the sweep's working
	 * set stays bounded to live keys. Distinct from the two above so the status a
	 * user sees does not change from EXPIRED to REVOKED on the day the cron runs.
	 */
	EXPIRY_RECORDED = 'EXPIRY_RECORDED',
}

/**
 * A key's state as presented to its owner.
 *
 * DERIVED, never stored: it is a reading of `revokedAt`, `revokedReason` and
 * `expiresAt` together. Persisting it would create a second source of truth that
 * a passing expiry date would silently falsify — a key expires with no write
 * touching its row.
 */
export enum ApiKeyStatus {
	ACTIVE = 'ACTIVE',
	EXPIRED = 'EXPIRED',
	REVOKED = 'REVOKED',
	BLOCKED = 'BLOCKED',
}
